import React, { useCallback, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckMark, ChevronDown } from '@lyearn/icons';
import { ActionMenuItemProps, MenuListItems } from '@lyearn/molecules';
import { Popover, Stack, Typography } from '@lyearn/ui';

import useSwitchWorkspace from '../../hooks/useSwitchWorkspace';
import useUserWorkspaces from '../../hooks/useUserWorkspaces';

function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const [showPopover, { toggle: togglePopover }] = useToggle(false);

  const { workspaces, currentWorkspace, personId, isLoading } = useUserWorkspaces();
  const { switchWorkspace, switching } = useSwitchWorkspace(personId);

  const items = useMemo<ActionMenuItemProps[]>(
    () =>
      workspaces.map(({ id, name }) => ({
        id,
        label: name,
        disabled: switching,
        EndIcon: currentWorkspace?.id === id ? CheckMark : undefined,
      })),
    [currentWorkspace?.id, switching, workspaces],
  );

  const onAction = useCallback(
    (workspaceId: string) => {
      togglePopover();
      const workspace = workspaces.find(({ id }) => id === workspaceId);
      if (workspace && workspace.id !== currentWorkspace?.id) {
        switchWorkspace(workspace);
      }
    },
    [currentWorkspace?.id, switchWorkspace, togglePopover, workspaces],
  );

  // A single workspace leaves nothing to switch to, so the logo stays on its own.
  if (isLoading || workspaces.length < 2) {
    return null;
  }

  return (
    <Popover open={showPopover} onOpenChange={togglePopover}>
      <Popover.Button aria-label={t('Common:SwitchWorkspace')} className="cursor-pointer">
        <Stack alignItems="center" className="gap-4 rounded-8 px-8 py-4 hover:bg-bg-hover">
          <Typography className="max-w-[16rem] truncate" variant="body-short-02">
            {currentWorkspace?.name ?? t('Common:SelectWorkspace')}
          </Typography>
          <ChevronDown className="h-16 w-16" />
        </Stack>
      </Popover.Button>
      <Popover.Content
        className="p-12 pt-16"
        maxWidth={20}
        minWidth={15}
        renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
        <Typography
          as="p"
          className="mb-8 px-8"
          color="text-text-secondary"
          variant="body-short-03">
          {t('Common:YourWorkspaces')}
        </Typography>
        <MenuListItems
          className="space-y-4 p-0 xs:mb-12 xs:px-8 xs:pb-32"
          items={items}
          onAction={onAction}
        />
      </Popover.Content>
    </Popover>
  );
}

export default React.memo(WorkspaceSwitcher);
